import { useState, useCallback } from 'react';
import { Card } from '../types/Card';
import { useLocalStorage } from './useLocalStorage';
import { apiClient } from '../services/apiClient';
import { GenerationRequest } from '../types/Topic';

interface UseCardManagementReturn {
  cards: Card[];
  setCards: (cards: Card[]) => void;
  togglePin: (cardId: string) => void;
  regenerateCard: (cardId: string) => Promise<void>;
  regenerateAllUnpinned: (request: GenerationRequest) => Promise<void>;
  updateCard: (cardId: string, updates: Partial<Card>) => void;
  isRegenerating: boolean;
  error: string | null;
}

export function useCardManagement(): UseCardManagementReturn {
  const [rawCards, setRawCards] = useLocalStorage<Card[]>('brain-break-cards', []);
  // Filter out invalid/error cards that may have been persisted
  const cards = rawCards.filter(c => c.prompts?.question);
  const setCards = (newCards: Card[] | ((prev: Card[]) => Card[])) => {
    if (typeof newCards === 'function') {
      setRawCards(prev => (newCards as (prev: Card[]) => Card[])(prev).filter(c => c.prompts?.question));
    } else {
      setRawCards(newCards.filter(c => c.prompts?.question));
    }
  };
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const togglePin = useCallback((cardId: string) => {
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.id === cardId
          ? { ...card, isPinned: !card.isPinned, updatedAt: new Date() }
          : card
      )
    );
  }, [setCards]);

  const regenerateCard = useCallback(async (cardId: string) => {
    setIsRegenerating(true);
    setError(null);

    try {
      const cardToRegenerate = cards.find((c) => c.id === cardId);
      if (!cardToRegenerate) {
        throw new Error('Card not found');
      }

      // Create a generation request for just this card's topic
      const request: GenerationRequest = {
        topics: [{
          name: cardToRegenerate.topic,
          subject: cardToRegenerate.subject,
          confidence: 1.0,
        }],
        options: {
          regenerateUnpinned: false,
        },
      };

      const response = await apiClient.generateCards(request);

      if (response.cards.length > 0) {
        const newCard = response.cards[0];
        setCards((prevCards) =>
          prevCards.map((card) =>
            card.id === cardId
              ? {
                  ...card,
                  prompts: newCard.prompts,
                  updatedAt: new Date(),
                }
              : card
          )
        );
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to regenerate card';
      setError(errorMessage);
      throw err;
    } finally {
      setIsRegenerating(false);
    }
  }, [cards, setCards]);

  const regenerateAllUnpinned = useCallback(async (request: GenerationRequest) => {
    setIsRegenerating(true);
    setError(null);

    try {
      // Filter out topics for pinned cards
      const pinnedTopics = new Set(
        cards.filter((card) => card.isPinned).map((card) => card.topic)
      );

      const unpinnedTopics = request.topics.filter(
        (topic) => !pinnedTopics.has(topic.name)
      );

      if (unpinnedTopics.length === 0) {
        setIsRegenerating(false);
        return;
      }

      const regenerateRequest: GenerationRequest = {
        ...request,
        topics: unpinnedTopics,
        options: {
          ...request.options,
          regenerateUnpinned: true,
        },
      };

      const response = await apiClient.generateCards(regenerateRequest);

      // Update only unpinned cards
      setCards((prevCards) => {
        const updatedCards = [...prevCards];
        const newCardsByTopic = new Map(
          response.cards.map((card) => [card.topic, card])
        );

        return updatedCards.map((card) => {
          if (card.isPinned) {
            return card;
          }

          const newCard = newCardsByTopic.get(card.topic);
          if (newCard) {
            return {
              ...card,
              prompts: newCard.prompts,
              updatedAt: new Date(),
            };
          }

          return card;
        });
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to regenerate cards';
      setError(errorMessage);
      throw err;
    } finally {
      setIsRegenerating(false);
    }
  }, [cards, setCards]);

  const updateCard = useCallback((cardId: string, updates: Partial<Card>) => {
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.id === cardId
          ? { ...card, ...updates, updatedAt: new Date() }
          : card
      )
    );
  }, [setCards]);

  return {
    cards,
    setCards,
    togglePin,
    regenerateCard,
    regenerateAllUnpinned,
    updateCard,
    isRegenerating,
    error,
  };
}
